// Global timezone utilities for the entire application
// This provides user-friendly timezone mapping and location information

// User-friendly timezone to location mapping
export const getTimezoneLocation = (timezone) => {
  const timezoneMap = {
    // User-friendly standard abbreviations with clear geographical applicability
    'IST': 'India (All states)',
    'PST': 'United States (Pacific - California, Washington, Oregon)',
    'MST': 'United States (Mountain - Colorado, Utah, Arizona)',
    'CST': 'United States (Central - Texas, Illinois, Missouri)',
    'EST': 'United States (Eastern - New York, Florida, Georgia)',
    'GMT': 'United Kingdom, Ireland (GMT and BST)',
    'CET': 'Central Europe (Germany, France, Italy, Spain)',
    'EET': 'Eastern Europe (Poland, Ukraine, Greece)',
    'JST': 'Japan',
    'KST': 'South Korea',
    'CST_ASIA': 'China, Singapore, Philippines, Malaysia',
    'SGT': 'Singapore',
    'GST': 'Gulf (UAE, Saudi Arabia, Kuwait, Oman)',
    'WIB': 'Indonesia (Western - Jakarta, Sumatra)',
    'WITA': 'Indonesia (Central - Bali, Sulawesi)',
    'WIT': 'Indonesia (Eastern - Papua, Maluku)',
    'AEST': 'Australia (Eastern - Sydney, Melbourne)',
    'AEST_QLD': 'Brisbane, Queensland (AEST, UTC+10:00)',
    'ACST': 'Australia (Central - Adelaide)',
    'AWST': 'Australia (Western - Perth)',
    'NZST': 'New Zealand',
    'SAST': 'South Africa',
    'EAT': 'East Africa (Kenya, Tanzania)',
    'WAT': 'West Africa (Nigeria, Ghana)',
    'CAT': 'Central Africa (Zambia, Zimbabwe)',
    'UTC': 'International (UTC)',
    'UTC+0': 'International (UTC)',
    'UTC+1': 'Central Europe (Germany, France, Italy, Spain)',
    'UTC+2': 'Eastern Europe (Poland, Ukraine, Greece)',
    'UTC+3': 'Eastern Europe (Russia - Moscow, Turkey)',
    'UTC+4': 'Middle East (UAE, Georgia, Armenia)',
    'UTC+5': 'Central Asia (Pakistan, Uzbekistan)',
    'UTC+5:30': 'India (All states)',
    'UTC+6': 'Central Asia (Kazakhstan, Bangladesh)',
    'UTC+7': 'Southeast Asia (Thailand, Vietnam, Indonesia - Jakarta)',
    'UTC+8': 'East Asia (China, Singapore, Philippines, Malaysia)',
    'UTC+9': 'East Asia (Japan, South Korea)',
    'UTC+10': 'Australia (Sydney, Melbourne)',
    'UTC+11': 'Australia (Brisbane), Pacific Islands',
    'UTC+12': 'New Zealand, Pacific Islands',
    'UTC-1': 'Atlantic Islands (Azores)',
    'UTC-2': 'Atlantic Islands',
    'UTC-3': 'South America (Brazil - São Paulo, Argentina)',
    'UTC-4': 'South America (Brazil - Manaus), Caribbean',
    'UTC-5': 'North America (Eastern US, Canada - Toronto)',
    'UTC-6': 'North America (Central US, Canada - Winnipeg)',
    'UTC-7': 'North America (Mountain US, Canada - Calgary)',
    'UTC-8': 'North America (Pacific US, Canada - Vancouver)',
    'UTC-9': 'North America (Alaska)',
    'UTC-10': 'North America (Hawaii)',
    'UTC-11': 'Pacific Islands',
    'UTC-12': 'Pacific Islands',
    
    // IANA timezone names (for backward compatibility)
    'America/New_York': 'United States (Eastern - New York, Florida, Georgia)',
    'America/Chicago': 'United States (Central - Texas, Illinois, Missouri)',
    'America/Denver': 'United States (Mountain - Colorado, Utah, Arizona)',
    'America/Los_Angeles': 'United States (Pacific - California, Washington, Oregon)',
    'Europe/London': 'United Kingdom, Ireland',
    'Europe/Paris': 'France',
    'Europe/Berlin': 'Germany',
    'Europe/Rome': 'Italy',
    'Europe/Madrid': 'Spain',
    'Asia/Kolkata': 'India (All states)',
    'Asia/Tokyo': 'Japan',
    'Asia/Seoul': 'South Korea',
    'Asia/Shanghai': 'China',
    'Asia/Singapore': 'Singapore',
    'Asia/Dubai': 'UAE (Dubai, Abu Dhabi)',
    'Australia/Sydney': 'Australia (Eastern - Sydney, Melbourne)',
    'Australia/Brisbane': 'Brisbane, Queensland (AEST, UTC+10:00)',
    'Australia/Perth': 'Australia (Western - Perth)',
    'Pacific/Auckland': 'New Zealand',
    'America/Anchorage': 'United States (Alaska)',
    'Pacific/Honolulu': 'United States (Hawaii)',
    'Europe/Helsinki': 'Eastern Europe (Finland)',
    'HST': 'United States (Hawaii)',
    'AKST': 'United States (Alaska)',
  };
  
  return timezoneMap[timezone] || `${timezone} (Unknown location)`;
};

// Get all available timezones with user-friendly names
export const getAvailableTimezones = () => {
  return [
    { value: 'UTC', label: 'UTC (International - Coordinated Universal Time)' },
    { value: 'IST', label: 'IST (India - All states)' },
    { value: 'EST', label: 'EST (US Eastern - New York, Florida, Georgia)' },
    { value: 'PST', label: 'PST (US Pacific - California, Washington, Oregon)' },
    { value: 'CST', label: 'CST (US Central - Texas, Illinois, Missouri)' },
    { value: 'MST', label: 'MST (US Mountain - Colorado, Utah, Arizona)' },
    { value: 'GMT', label: 'GMT/BST (UK, Ireland — London clock)' },
    { value: 'CET', label: 'CET (Central Europe - Germany, France, Italy, Spain)' },
    { value: 'EET', label: 'EET (Eastern Europe - Poland, Ukraine, Greece)' },
    { value: 'JST', label: 'JST (Japan)' },
    { value: 'KST', label: 'KST (South Korea)' },
    { value: 'AEST', label: 'AEST (Australia Eastern - Sydney, Melbourne)' },
    { value: 'AEST_QLD', label: 'Brisbane, Queensland (AEST, UTC+10:00)' },
    { value: 'AWST', label: 'AWST (Australia Western - Perth)' },
    { value: 'ACST', label: 'ACST (Australia Central - Adelaide)' },
    { value: 'NZST', label: 'NZST (New Zealand)' },
    { value: 'SGT', label: 'SGT (Singapore)' },
    { value: 'GST', label: 'GST (Gulf - UAE, Saudi Arabia, Kuwait, Oman)' },
    { value: 'WIB', label: 'WIB (Indonesia Western - Jakarta, Sumatra)' },
    { value: 'WITA', label: 'WITA (Indonesia Central - Bali, Sulawesi)' },
    { value: 'WIT', label: 'WIT (Indonesia Eastern - Papua, Maluku)' },
    { value: 'SAST', label: 'SAST (South Africa)' },
    { value: 'EAT', label: 'EAT (East Africa - Kenya, Tanzania)' },
    { value: 'WAT', label: 'WAT (West Africa - Nigeria, Ghana)' },
    { value: 'CAT', label: 'CAT (Central Africa - Zambia, Zimbabwe)' },
    { value: 'HST', label: 'HST (US Hawaii)' },
    { value: 'AKST', label: 'AKST (US Alaska)' },
    { value: 'CST_ASIA', label: 'CST (China / East Asia)' }
  ];
};

const getValidTimezoneSelectValues = () => getAvailableTimezones().map((tz) => tz.value);

// Get timezone options for dropdowns
export const getTimezoneOptions = () => {
  return getAvailableTimezones().map(tz => ({
    value: tz.value,
    label: tz.label
  }));
};

// Format timezone display with location
export const formatTimezoneDisplay = (timezone) => {
  if (!timezone) return 'Not specified';
  
  const location = getTimezoneLocation(timezone);
  return `${timezone} - ${location}`;
};

// IANA (and legacy DB values) → edit-form abbreviation
const IANA_TO_ABBREVIATION = {
  'Asia/Kolkata': 'IST',
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Anchorage': 'AKST',
  'Pacific/Honolulu': 'HST',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Berlin': 'CET',
  'Europe/Helsinki': 'EET',
  'Europe/Rome': 'CET',
  'Europe/Madrid': 'CET',
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Shanghai': 'CST_ASIA',
  'Asia/Singapore': 'SGT',
  'Asia/Dubai': 'GST',
  'Asia/Riyadh': 'GST',
  'Asia/Kuwait': 'GST',
  'Asia/Muscat': 'GST',
  'Asia/Jakarta': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
  'Europe/Stockholm': 'CET',
  'Europe/Amsterdam': 'CET',
  'Australia/Sydney': 'AEST',
  'Australia/Brisbane': 'AEST_QLD',
  'Australia/Perth': 'AWST',
  'Pacific/Auckland': 'NZST',
};

// Get timezone abbreviation from IANA timezone name (or pass-through if already an abbrev)
export const getTimezoneAbbreviation = (ianaTimezone) => {
  if (!ianaTimezone) return 'UTC';
  const key = String(ianaTimezone).trim();
  if (IANA_TO_ABBREVIATION[key]) return IANA_TO_ABBREVIATION[key];
  const valid = getValidTimezoneSelectValues();
  if (valid.includes(key)) return key;
  return key;
};

/**
 * Value for MUI timezone Select: prefers a known option; keeps unknown values for a legacy MenuItem.
 */
export const normalizeTimezoneForFormSelect = (stored) => {
  if (stored == null || stored === '') return 'UTC';
  const abbr = getTimezoneAbbreviation(stored);
  const valid = getValidTimezoneSelectValues();
  if (valid.includes(abbr)) return abbr;
  if (valid.includes(stored)) return stored;
  return String(stored).trim();
};

/** Dropdown label for a stored abbreviation or IANA timezone. */
export const getTimezoneOptionLabel = (stored) => {
  if (stored == null || String(stored).trim() === '') return '';
  const value = normalizeTimezoneForFormSelect(stored);
  const match = getAvailableTimezones().find((tz) => tz.value === value);
  return match?.label || String(stored).trim();
};

/** Store IANA in DB when possible; keeps existing abbrev/legacy values unchanged. */
export const normalizeTimezoneForStorage = (formValue) => {
  if (formValue == null || formValue === '') return 'UTC';
  const v = String(formValue).trim();
  return getIANATimezone(v) || v;
};

// Get IANA timezone name from abbreviation
export const getIANATimezone = (abbreviation) => {
  const ianaMap = {
    'IST': 'Asia/Kolkata',
    'EST': 'America/New_York',
    'CST': 'America/Chicago',
    'MST': 'America/Denver',
    'PST': 'America/Los_Angeles',
    'HST': 'Pacific/Honolulu',
    'AKST': 'America/Anchorage',
    'GMT': 'Europe/London',
    'BST': 'Etc/GMT-1',
    'CET': 'Europe/Paris',
    'EET': 'Europe/Helsinki',
    'JST': 'Asia/Tokyo',
    'KST': 'Asia/Seoul',
    'CST_ASIA': 'Asia/Shanghai',
    'AEST': 'Australia/Sydney',
    'AEST_QLD': 'Australia/Brisbane',
    'AWST': 'Australia/Perth',
    'ACST': 'Australia/Adelaide',
    'NZST': 'Pacific/Auckland',
    'SGT': 'Asia/Singapore',
    'GST': 'Asia/Dubai',
    'WIB': 'Asia/Jakarta',
    'WITA': 'Asia/Makassar',
    'WIT': 'Asia/Jayapura',
    'UTC': 'UTC',
  };

  if (!abbreviation) return 'UTC';
  const key = String(abbreviation).trim();
  if (IANA_TO_ABBREVIATION[key]) return key;
  return ianaMap[key] || (key.includes('/') ? key : null);
};

// Validate timezone
export const isValidTimezone = (timezone) => {
  const validTimezones = getAvailableTimezones().map(tz => tz.value);
  return validTimezones.includes(timezone);
};

// Get timezone suggestions based on country
export const getTimezoneSuggestions = (country) => {
  const suggestions = {
    'India': ['IST'],
    'USA': ['EST', 'CST', 'MST', 'PST'],
    'United States': ['EST', 'CST', 'MST', 'PST'],
    'UK': ['GMT'],
    'United Kingdom': ['GMT'],
    'Germany': ['CET'],
    'France': ['CET'],
    'Italy': ['CET'],
    'Spain': ['CET'],
    'Japan': ['JST'],
    'South Korea': ['KST'],
    'Australia': ['AEST', 'AEST_QLD', 'AWST', 'ACST'],
    'New Zealand': ['NZST'],
    'Singapore': ['SGT'],
    'UAE': ['GST'],
    'Kuwait': ['GST'],
    'Oman': ['GST'],
    'Saudi Arabia': ['GST'],
    'Netherlands': ['CET'],
    'Sweden': ['CET'],
    'Indonesia': ['WIB', 'WITA', 'WIT'],
    'South Africa': ['SAST'],
    'Kenya': ['EAT'],
    'Nigeria': ['WAT'],
    'Zambia': ['CAT'],
    'Canada': ['EST', 'CST', 'MST', 'PST'],
  };
  
  return suggestions[country] || ['UTC'];
};

const timezoneUtils = {
  getTimezoneLocation,
  getAvailableTimezones,
  getTimezoneOptions,
  formatTimezoneDisplay,
  getTimezoneAbbreviation,
  getIANATimezone,
  normalizeTimezoneForFormSelect,
  getTimezoneOptionLabel,
  normalizeTimezoneForStorage,
  isValidTimezone,
  getTimezoneSuggestions
};

export default timezoneUtils;
